import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '../stores/uiStore'
import { useQueryStore } from '../stores/queryStore'
import { copyToClipboard } from '../lib/clipboard'

export function useKeyboardShortcuts() {
  const navigate = useNavigate()
  const { activeTab, setActiveTab, showToast } = useUIStore()
  const { response, submitQuery, clearResponse, query, isLoading } = useQueryStore()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle when not typing in an input
      const target = e.target as HTMLElement
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)

      // Cmd/Ctrl + Enter - Submit (works even in textarea)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        if (!isLoading && query.trim()) {
          submitQuery()
        }
        return
      }

      // Skip other shortcuts if typing
      if (isTyping) return

      // Cmd/Ctrl + 1/2/3/4 - Switch tabs
      if ((e.metaKey || e.ctrlKey) && ['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault()
        const tabs = { '1': 'twitter', '2': 'facebook', '3': 'forum', '4': 'infographic' } as const
        setActiveTab(tabs[e.key as '1' | '2' | '3' | '4'])
        return
      }

      // Cmd/Ctrl + Shift + C - Copy active response
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault()
        if (response) {
          let content: string
          if (activeTab === 'infographic') {
            const ig = response.infographic
            // New format with prompt, or old format with title/keyVisuals
            content = ig.prompt
              ? `INFOGRAPHIC PROMPT:\n\n${ig.prompt}\n\n---\nThis infographic is for illustrative purposes only and may contain errors. Always refer to official Canon resources for the most accurate information.`
              : `INFOGRAPHIC CONCEPT\n\nTITLE: ${ig.title || 'N/A'}\n\nKEY VISUAL ELEMENTS:\n${(ig.keyVisuals || []).map(v => `- ${v}`).join('\n')}\n\nPRIMARY TAKEAWAY:\n${ig.primaryTakeaway || 'N/A'}\n\nCALL TO ACTION:\n${ig.cta || 'N/A'}\n\n---\nThis infographic is for illustrative purposes only and may contain errors. Always refer to official Canon resources for the most accurate information.`
          } else {
            content = {
              twitter: response.tweet,
              facebook: response.facebook,
              forum: response.forum,
            }[activeTab]
          }

          copyToClipboard(content).then((success) => {
            if (success) {
              showToast(`Copied ${activeTab} response`, 'success')
            }
          })
        }
        return
      }

      // Cmd/Ctrl + N - New query
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault()
        clearResponse()
        navigate('/')
        return
      }

      // Cmd/Ctrl + K - Focus search (on history page)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        navigate('/history')
        // Focus will be handled by the history page
        return
      }

      // Escape - Close modals / clear focus
      if (e.key === 'Escape') {
        const activeElement = document.activeElement as HTMLElement
        activeElement?.blur()
        return
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    activeTab,
    response,
    query,
    isLoading,
    setActiveTab,
    submitQuery,
    clearResponse,
    showToast,
    navigate,
  ])
}
